import type { CalcStep } from "@/lib/calc/steps";

export interface TipInput { bill: number; tax: number; tipPct: number; people: number; tipOnPreTax: boolean; roundUpPerPerson: boolean; digits: number; /** A fixed tip amount instead of a percentage (for example a hotel housekeeping tip). */ flatTip?: number }
export interface TipResult { tip: number; total: number; shares: number[]; perPersonLabel: string; allEqual: boolean }

/** All maths in minor units (cents) so shares always add up to the total exactly. */
export function computeTip(i: TipInput): TipResult {
  const f = 10 ** i.digits;
  const bill = Math.round(i.bill * f), tax = Math.round(i.tax * f);
  const base = i.tipOnPreTax ? bill : bill + tax;
  let tip = i.flatTip !== undefined ? Math.round(i.flatTip * f) : Math.round((base * i.tipPct) / 100);
  let total = bill + tax + tip;
  const n = i.people;
  if (i.roundUpPerPerson && n > 0) {
    const unit = f; // whole currency units
    const share = Math.ceil(total / n / unit) * unit;
    const newTotal = share * n;
    tip += newTotal - total;
    total = newTotal;
  }
  const baseShare = Math.floor(total / n), extra = total - baseShare * n;
  const shares = Array.from({ length: n }, (_, k) => (k < extra ? baseShare + 1 : baseShare)).map((c) => c / f);
  return { tip: tip / f, total: total / f, shares, perPersonLabel: "", allEqual: extra === 0 };
}

export type TipCheck = { ok: true; bill: number; tax: number; people: number } | { ok: false; message: string };

export function validate(billStr: string, taxStr: string, peopleStr: string): TipCheck {
  const bill = billStr.trim() === "" ? 0 : Number(billStr), tax = taxStr.trim() === "" ? 0 : Number(taxStr), people = Number(peopleStr);
  if (!Number.isFinite(bill) || bill < 0) return { ok: false, message: "The bill must be zero or more." };
  if (!Number.isFinite(tax) || tax < 0) return { ok: false, message: "Tax must be zero or more." };
  if (!Number.isInteger(people) || people < 1) return { ok: false, message: "People must be a whole number, 1 or more." };
  if (people > 500) return { ok: false, message: "Please use 500 people or fewer." };
  if (bill > 1e9) return { ok: false, message: "That bill is too large." };
  return { ok: true, bill, tax, people };
}

export type PctCheck = { ok: true; pct: number; warning?: string } | { ok: false; message: string };

/** Tip percentage: 150% is rare but real (a very small bill, a generous thank-you), so over 100% only warns. */
export function checkTipPct(raw: string): PctCheck {
  if (raw.trim() === "") return { ok: false, message: "Enter a tip percentage, for example 18." };
  const pct = Number(raw);
  if (!Number.isFinite(pct)) return { ok: false, message: "The tip must be a number, for example 18." };
  if (pct < 0) return { ok: false, message: "The tip can't be negative." };
  if (pct > 1000) return { ok: false, message: "That tip percentage is too large (1000% or less)." };
  if (pct > 100) return { ok: true, pct, warning: `A ${pct}% tip is more than the bill itself. Check this is what you meant.` };
  return { ok: true, pct };
}

/** Whether the tip starts out on the pre-tax amount: the US norm. Other currencies start on bill plus tax. */
export const defaultPreTax = (currency: string): boolean => currency === "USD";

/** Where tipping is customary, a sensible starting percentage for the currency's main country. */
export const TIP_HINTS: Record<string, string> = {
  USD: "In the US, 15–20% is usual in restaurants.",
  CAD: "In Canada, 15–20% is usual in restaurants.",
  GBP: "In the UK, 10–12.5% is common, often added as a service charge. Check your bill first.",
  EUR: "In much of Europe, service is included; rounding up or 5–10% is plenty.",
  INR: "In India, 5–10% is typical if no service charge was added.",
  JPY: "In Japan, tipping is not customary and can cause confusion.",
  AUD: "In Australia, tipping is optional; 10% for great service.",
  CNY: "In mainland China, tipping is generally not expected.",
  KRW: "In South Korea, tipping is not customary.",
};

export const CURRENCIES = ["USD", "EUR", "GBP", "INR", "CAD", "AUD", "JPY", "CHF", "CNY", "MXN", "BRL", "KRW", "SGD", "AED", "ZAR", "SEK", "NZD", "TRY", "THB"];

const REGION_CURRENCY: Record<string, string> = {
  US: "USD", GB: "GBP", IN: "INR", CA: "CAD", AU: "AUD", JP: "JPY", CH: "CHF", CN: "CNY", MX: "MXN", BR: "BRL", KR: "KRW", SG: "SGD", AE: "AED", ZA: "ZAR", SE: "SEK", NZ: "NZD", TR: "TRY", TH: "THB",
  DE: "EUR", FR: "EUR", ES: "EUR", IT: "EUR", NL: "EUR", IE: "EUR", PT: "EUR", AT: "EUR", BE: "EUR", FI: "EUR", GR: "EUR",
};
export function defaultCurrency(locale?: string): string {
  const region = locale?.split("-")[1]?.toUpperCase();
  return (region && REGION_CURRENCY[region]) || "USD";
}

export function fractionDigits(currency: string): number {
  try { return new Intl.NumberFormat(undefined, { style: "currency", currency }).resolvedOptions().maximumFractionDigits ?? 2; } catch { return 2; }
}

/* ---------------- B-0090: comparison strip, service and country guide, uneven split, reverse mode ---------------- */

export const COMPARE_PCTS = [10, 15, 18, 20, 25];
export interface StripRow { pct: number; tip: number; total: number; perPerson: number }
/** Tip, total and per-person amount at several percentages at once, using the same cent maths as the main result. */
export function tipStrip(i: Omit<TipInput, "tipPct" | "roundUpPerPerson" | "flatTip">, pcts: number[] = COMPARE_PCTS): StripRow[] {
  return pcts.map((pct) => {
    const r = computeTip({ ...i, tipPct: pct, roundUpPerPerson: false });
    return { pct, tip: r.tip, total: r.total, perPerson: Math.max(...r.shares) };
  });
}

export interface ServicePreset { id: string; label: string; pct?: number; flat?: number; range: string; note: string }
/**
 * Service presets: common US etiquette ranges (consumer etiquette guides such as the Emily Post Institute). They are conventions, not rules,
 * and vary by region and venue; the page says so. The preset applies the middle of the range; housekeeping is a flat amount per night.
 */
export const SERVICE_PRESETS: ServicePreset[] = [
  { id: "restaurant", label: "Restaurant (sit-down)", pct: 18, range: "15-20%", note: "Sit-down restaurant: 15 to 20% of the bill, more for exceptional service. Check for an automatic service charge first." },
  { id: "delivery", label: "Delivery", pct: 18, range: "15-20%", note: "Food delivery: about 15 to 20%, and consider a larger share on a small order or in bad weather." },
  { id: "bar", label: "Bar", pct: 18, range: "15-20%", note: "Bar tab: 15 to 20%, or about $1 to $2 a drink on a small tab." },
  { id: "salon", label: "Hair salon or spa", pct: 18, range: "15-20%", note: "Hair, spa and similar services: 15 to 20% of the service price." },
  { id: "taxi", label: "Taxi or rideshare", pct: 15, range: "10-20%", note: "Taxi and rideshare: about 10 to 20% of the fare." },
  { id: "housekeeping", label: "Hotel housekeeping", flat: 3, range: "$2-5 per night", note: "Hotel housekeeping: about $2 to $5 per night, left each day for the person who cleaned that day. This is a flat amount, not a percentage." },
];

export interface CountryTip { customary: "expected" | "optional" | "not customary" | "included"; text: string }
/**
 * Guidance by currency (its main country). The text is the page's long-standing hint; `customary` is a short label used for the badge.
 * General etiquette, not a rule: always check the bill for a service charge.
 */
export const COUNTRY_TIPS: Record<string, CountryTip> = {
  USD: { customary: "expected", text: TIP_HINTS.USD },
  CAD: { customary: "expected", text: TIP_HINTS.CAD },
  GBP: { customary: "optional", text: TIP_HINTS.GBP },
  EUR: { customary: "included", text: TIP_HINTS.EUR },
  INR: { customary: "optional", text: TIP_HINTS.INR },
  JPY: { customary: "not customary", text: TIP_HINTS.JPY },
  AUD: { customary: "optional", text: TIP_HINTS.AUD },
  CNY: { customary: "not customary", text: TIP_HINTS.CNY },
  KRW: { customary: "not customary", text: TIP_HINTS.KRW },
};

export interface UnevenShare { own: number; taxShare: number; tipShare: number; total: number }
export interface UnevenResult { shares: UnevenShare[]; tip: number; total: number; itemsTotal: number }
/**
 * Uneven split: each person pays their own amount plus a pro-rata share of the tax and of the tip. In cents, with the largest-remainder rule,
 * so the shares add up to the total exactly. People with 0 pay 0.
 */
export function splitUneven(items: number[], tax: number, tipPct: number, tipOnPreTax: boolean, digits: number): UnevenResult {
  const f = 10 ** digits;
  const own = items.map((x) => Math.round(x * f)), t = Math.round(tax * f);
  const sum = own.reduce((a, b) => a + b, 0);
  const tip = Math.round(((tipOnPreTax ? sum : sum + t) * tipPct) / 100);
  const allocate = (amount: number): number[] => {
    if (sum === 0) return own.map(() => 0);
    const raw = own.map((o) => (amount * o) / sum), base = raw.map(Math.floor);
    let left = amount - base.reduce((a, b) => a + b, 0);
    const order = raw.map((r, k) => ({ k, rem: r - base[k] })).sort((a, b) => b.rem - a.rem || a.k - b.k);
    for (const o of order) { if (left <= 0) break; base[o.k]++; left--; }
    return base;
  };
  const taxA = allocate(t), tipA = allocate(tip);
  const shares = own.map((o, k) => ({ own: o / f, taxShare: taxA[k] / f, tipShare: tipA[k] / f, total: (o + taxA[k] + tipA[k]) / f }));
  return { shares, tip: tip / f, total: (sum + t + tip) / f, itemsTotal: sum / f };
}

export type ReverseResult = { ok: true; tip: number; pct: number } | { ok: false; message: string };
/** Reverse mode: from what was paid, the tip left and its percentage of the bill (before tax when `tipOnPreTax`, otherwise of bill plus tax). */
export function impliedTip(bill: number, tax: number, paid: number, tipOnPreTax: boolean): ReverseResult {
  if (![bill, tax, paid].every(Number.isFinite) || bill < 0 || tax < 0 || paid < 0) return { ok: false, message: "Enter amounts of zero or more." };
  const base = tipOnPreTax ? bill : bill + tax;
  if (!(base > 0)) return { ok: false, message: "Enter a bill greater than 0 to work out the percentage." };
  const tip = Math.round((paid - bill - tax) * 1e6) / 1e6;
  if (tip < 0) return { ok: false, message: "The amount paid is less than the bill plus tax, so there is no tip to measure." };
  return { ok: true, tip, pct: Math.round((tip / base) * 1e6) / 1e4 };
}

export function tipSteps(i: TipInput, r: TipResult, m: (n: number) => string): CalcStep[] {
  const base = i.tipOnPreTax ? i.bill : i.bill + i.tax;
  const steps: CalcStep[] = [
    { label: "Amount the tip is based on", formula: i.tipOnPreTax ? "bill (before tax)" : "bill + tax", inputs: { bill: m(i.bill), tax: m(i.tax) }, result: m(base) },
    i.flatTip !== undefined
      ? { label: "Tip", formula: "the flat amount you chose", inputs: { amount: m(i.flatTip) }, result: m(i.flatTip) }
      : { label: "Tip", formula: "base x tip % / 100", inputs: { base: m(base), "tip %": `${i.tipPct}%` }, result: m(base * i.tipPct / 100) },
    { label: "Total", formula: "bill + tax + tip", inputs: { bill: m(i.bill), tax: m(i.tax), tip: m(r.tip) }, result: m(r.total) },
  ];
  if (i.people > 1) steps.push({ label: "Per person", formula: "total / people, shared out in cents so the shares add up exactly", inputs: { total: m(r.total), people: i.people }, result: r.allEqual ? m(r.shares[0]) : `${m(Math.max(...r.shares))} or ${m(Math.min(...r.shares))}` });
  return steps;
}

/* ---------------- share link ---------------- */
export interface TipShare { bill: string; tax: string; tipPct: string; people: string; currency: string; preTax: "" | "1" | "0"; roundUp: boolean }
export const TIP_SHARE_KEYS: Record<keyof TipShare, string> = { bill: "bill", tax: "tax", tipPct: "tip", people: "n", currency: "cur", preTax: "pre", roundUp: "up" };
const NUM = /^\d{0,10}(\.\d{0,4})?$/;
export function tipToQuery(s: TipShare, defaults: TipShare): string {
  const p = new URLSearchParams();
  (Object.keys(TIP_SHARE_KEYS) as (keyof TipShare)[]).forEach((k) => { if (s[k] !== defaults[k]) p.set(TIP_SHARE_KEYS[k], s[k] === true ? "1" : s[k] === false ? "0" : String(s[k])); });
  return p.toString();
}
export function tipFromQuery(search: string, defaults: TipShare): TipShare {
  const p = new URLSearchParams(search), out: TipShare = { ...defaults };
  for (const k of ["bill", "tax", "tipPct", "people"] as const) { const v = p.get(TIP_SHARE_KEYS[k]); if (v !== null && NUM.test(v)) out[k] = v; }
  const cur = p.get("cur"); if (cur && CURRENCIES.includes(cur)) out.currency = cur;
  const pre = p.get("pre"); if (pre === "1" || pre === "0") out.preTax = pre;
  if (p.get("up") === "1") out.roundUp = true;
  return out;
}
